import { z } from "zod";
import { adminBillingSettingsSchema, billingOrderStatusSchema } from "./billing.js";
import type {
  DeviceView,
  TenantLicensePlan,
  TenantLicenseStatus
} from "./common.js";
import type {
  AdminBillingSettingsInput,
  AdminBillingSettingsView,
  BillingOrderView
} from "./billing.js";
import type { UsageQuotaView } from "./usage.js";
import type { AdminRuleBundleView } from "./rule-bundles.js";

const optionalTrimmedString = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
    schema.optional()
  );

function publicCollectProxyHostDoesNotContainPort(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed.includes(":")) return true;
  if (/^\[[^\]]+\](?::\d+)?$/.test(trimmed)) return false;
  if (/^[^:]+:\d+$/.test(trimmed)) return false;
  // IPv6 literal without brackets is accepted; runtime URL builder will bracket it.
  return /^[0-9a-fA-F:.]+$/.test(trimmed);
}

export const adminLoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1)
});

export const adminLicensePlanSchema = z.enum(["monthly", "quarterly", "yearly"]);

const dateTimeStringSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => Number.isFinite(new Date(value).getTime()), {
    message: "日期时间不合法"
  });

export const adminLicenseSchema = z.object({
  plan: adminLicensePlanSchema,
  status: z.enum(["active", "disabled", "expired"]),
  entitlements: z.array(z.string().trim().min(1)).min(1).max(100),
  deviceLimit: z.number().int().min(1).max(50),
  /**
   * 套餐允许该账号创建的店铺数量上限。
   *
   * - 默认 50（中型套餐基线）；max 上限 10000 给运营预留扩展面。
   * - 0 视作「禁止新建」，保留入口给管理员临时锁定某个账号。
   * - 桌面端 `ShudianCore::create_store` 在写入前会基于此值兜底校验，
   *   超限返回中文错误「店铺数量已达套餐上限（N/N）...」。
   */
  storeLimit: z.number().int().min(0).max(10000).default(50),
  expiresAt: dateTimeStringSchema
});

export const adminTenantCreateSchema = z.object({
  id: z.string().trim().min(2).max(64),
  name: z.string().trim().min(1).max(120),
  ownerEmail: optionalTrimmedString(z.string().trim().email()),
  ownerName: optionalTrimmedString(z.string().trim().min(1).max(120)),
  ownerPassword: optionalTrimmedString(
    z
      .string()
      .min(8)
      .max(200)
      .refine((value) => value.trim().length > 0)
  ),
  license: adminLicenseSchema.optional()
}).superRefine((value, ctx) => {
  if (value.ownerPassword && !value.ownerEmail) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["ownerEmail"],
      message: "设置初始密码时必须填写登录邮箱"
    });
  }
});

export const adminTenantProfileSchema = z.object({
  name: z.string().trim().min(1).max(120)
});

export const adminTenantParamsSchema = z.object({
  tenantId: z.string().trim().min(1)
});

/**
 * 登录账号密码重置（admin 操作）。
 *
 * 单账号化后登录账号密码由 admin 直接重置，不再有其它登录身份的密码重置概念。
 */
export const adminTenantOwnerPasswordResetSchema = z.object({
  password: z
    .string()
    .min(8)
    .max(200)
    .refine((value) => value.trim().length > 0)
});

export const adminDeviceRevokeSchema = z.object({
  deviceId: z.string().trim().min(1)
});

export const publicCollectProxyProtocolSchema = z.enum([
  "http",
  "https",
  "socks4",
  "socks4a",
  "socks5",
  "socks5h"
]);

const publicCollectProxyHostSchema = z
  .string()
  .trim()
  .min(1)
  .max(253)
  .refine((value) => !/^\w+:\/\//.test(value), {
    message: "代理主机只填写域名或 IP，不要填写协议"
  })
  .refine((value) => !/[\s/@]/.test(value), {
    message: "代理主机不能包含空格、路径或账号信息"
  })
  .refine((value) => publicCollectProxyHostDoesNotContainPort(value), {
    message: "代理主机不能包含端口，请把端口填写到端口字段"
  });

export const adminPublicCollectProxyEntrySchema = z
  .object({
    id: optionalTrimmedString(z.string().trim().min(1).max(80)),
    enabled: z.boolean(),
    name: z.string().trim().min(1).max(120),
    protocol: publicCollectProxyProtocolSchema,
    host: publicCollectProxyHostSchema,
    port: z.number().int().min(1).max(65_535),
    username: optionalTrimmedString(z.string().trim().max(200)),
    /**
     * 非空：重写密码；undefined / 空串：保留现有密码。
     * Admin 视图永远不返回该字段。
     */
    password: optionalTrimmedString(z.string().trim().min(1).max(512)),
    /** true：清除现有密码；与 password 互斥。 */
    clearPassword: z.boolean().optional()
  })
  .superRefine((value, ctx) => {
    if (value.password && value.clearPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["password"],
        message: "不能同时设置新密码与清除密码"
      });
    }
  });

export const adminPublicCollectProxyPoolSchema = z
  .object({
    proxies: z.array(adminPublicCollectProxyEntrySchema).max(50)
  })
  .superRefine((value, ctx) => {
    const ids = new Set<string>();
    for (const [index, proxy] of value.proxies.entries()) {
      if (!proxy.id) continue;
      if (ids.has(proxy.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["proxies", index, "id"],
          message: "代理 ID 不能重复"
        });
      }
      ids.add(proxy.id);
    }
  });

export const adminAuditActionSchema = z.enum([
  "tenant.create",
  "tenant.profile.update",
  "tenant.license.update",
  "tenant.device.revoke",
  "tenant.user.create",
  "tenant.user.update",
  "tenant.user.role.update",
  "tenant.user.password.reset",
  "tenant.user.sessions.revoke",
  "system.public_collect_proxy.update",
  "cloud.auth.login.success",
  "cloud.auth.login.failure",
  "cloud.auth.register.success",
  "cloud.auth.register.failure",
  "cloud.auth.refresh.success",
  "cloud.auth.refresh.failure",
  "cloud.auth.logout",
  "cloud.device.activate.success",
  "cloud.device.activate.failure",
  "cloud.device.revoke",
  "cloud.device.revoke.failure",
  "cloud.entitlement_lease.issue.success",
  "cloud.entitlement_lease.issue.failure",
  "cloud.usage.quota.exceeded",
  "cloud.billing.checkout_session.create",
  "cloud.billing.webhook.processed",
  "cloud.billing.webhook.failure",
  "billing.settings.update",
  "cloud.rule_bundle.fetch.success",
  "cloud.rule_bundle.fetch.failure",
  "cloud.public_collect.product.success",
  "cloud.public_collect.product.failure",
  "cloud.public_collect.search.success",
  "cloud.public_collect.search.failure",
  "rule_bundle.publish",
  "desktop_update.publish",
  "desktop_update.upload_publish",
  "desktop_update.update",
  "desktop_update.enable",
  "desktop_update.disable",
  "cloud.sync.key_envelope.init",
  "cloud.sync.root_key.fetch",
  "cloud.sync.device.register",
  "cloud.sync.device.revoke",
  "cloud.sync.push",
  "cloud.sync.pull",
  "cloud.sync.blob.upload",
  "cloud.sync.blob.download",
  "cloud.sync.rejected",
  "cloud.admin.sync.device.revoke",
  "cloud.admin.sync.root_key.rotate"
]);

/**
 * 审计动作的发起方类别。
 *
 * - `admin`：cloud-admin 管理员；
 * - `tenant_owner`：合并 schema 后取代旧 `cloud_user`，对应 cloud_tenants 行的登录账号；
 * - `cloud_user`：保留以兼容历史 cloud_audit_entries 行（合并前写入的旧记录）；
 * - `anonymous` / `system`：未鉴权 / 系统调度。
 */
export const adminAuditActorTypeSchema = z.enum([
  "admin",
  "tenant_owner",
  "cloud_user",
  "anonymous",
  "system"
]);

export const adminAuditQuerySchema = z.object({
  tenantId: z.string().trim().min(1).optional(),
  action: adminAuditActionSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional()
});

export const adminBillingOrderQuerySchema = z.object({
  tenantId: z.string().trim().min(1).optional(),
  status: billingOrderStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional()
});

export const adminBillingSettingsUpdateSchema = adminBillingSettingsSchema;

export type AdminLoginInput = z.infer<typeof adminLoginSchema>;
export type AdminLicensePlan = z.infer<typeof adminLicensePlanSchema>;
export type AdminLicenseInput = z.infer<typeof adminLicenseSchema>;
export type AdminTenantCreateInput = z.infer<typeof adminTenantCreateSchema>;
export type AdminTenantProfileInput = z.infer<typeof adminTenantProfileSchema>;
export type AdminTenantParams = z.infer<typeof adminTenantParamsSchema>;
export type AdminTenantOwnerPasswordResetInput = z.infer<typeof adminTenantOwnerPasswordResetSchema>;
export type AdminDeviceRevokeInput = z.infer<typeof adminDeviceRevokeSchema>;
export type PublicCollectProxyProtocol = z.infer<typeof publicCollectProxyProtocolSchema>;
export type AdminPublicCollectProxyEntryInput = z.infer<typeof adminPublicCollectProxyEntrySchema>;
export type AdminPublicCollectProxyPoolInput = z.infer<typeof adminPublicCollectProxyPoolSchema>;
export type AdminAuditAction = z.infer<typeof adminAuditActionSchema>;
export type AdminAuditActorType = z.infer<typeof adminAuditActorTypeSchema>;
export type AdminAuditQuery = z.infer<typeof adminAuditQuerySchema>;
export type AdminBillingOrderQuery = z.infer<typeof adminBillingOrderQuerySchema>;
export type AdminBillingSettingsUpdateInput = AdminBillingSettingsInput;

export interface AdminPublicCollectProxyEntryView {
  id: string;
  enabled: boolean;
  name: string;
  protocol: PublicCollectProxyProtocol;
  host: string;
  port: number;
  username?: string;
  passwordConfigured: boolean;
  updatedAt: string;
}

export interface AdminPublicCollectProxyPoolView {
  proxies: AdminPublicCollectProxyEntryView[];
  enabledCount: number;
  updatedAt: string | null;
}

export interface AdminPublicCollectProxyPoolResponse {
  pool: AdminPublicCollectProxyPoolView;
}

export interface AdminIdentity {
  id: string;
  email: string;
  name: string;
  permissions: string[];
}

export interface AdminSession {
  admin: AdminIdentity;
  accessToken: string;
  expiresAt: string;
}

export interface TenantLicenseInput {
  plan: TenantLicensePlan;
  status: TenantLicenseStatus;
  entitlements: string[];
  deviceLimit: number;
  /** 套餐允许的店铺数量上限，默认 50；与 adminLicenseSchema.storeLimit 保持一致。 */
  storeLimit: number;
  expiresAt: string;
}

export interface TenantLicenseView extends TenantLicenseInput {
  tenantId: string;
  updatedAt: string;
}

export interface AdminTenantSummary {
  id: string;
  name: string;
  /** 登录邮箱（合并 schema 后取自 cloud_tenants.owner_email）。 */
  ownerEmail: string;
  /** 该账号已创建的店铺数量；用于运营查看「已用 / 上限」。 */
  storeCount: number;
  activeDeviceCount: number;
  revokedDeviceCount: number;
  activeSessionCount: number;
  license: TenantLicenseView;
}

export interface AdminTenantDetail extends AdminTenantSummary {
  /** 登录账号显示名（可选）。 */
  ownerName?: string;
  /** 登录账号状态。 */
  ownerStatus: "active" | "disabled";
  /** 登录账号密码上次更新时间。 */
  ownerPasswordUpdatedAt?: string;
  devices: DeviceView[];
}

export interface AdminTenantOverviewItem extends AdminTenantSummary {
  quotas: UsageQuotaView[];
}

export interface AdminTenantDetailResponse {
  tenant: AdminTenantDetail;
  quotas: UsageQuotaView[];
}

export interface AdminOverview {
  totals: {
    tenantCount: number;
    userCount: number;
    activeDeviceCount: number;
    revokedDeviceCount: number;
    activeSessionCount: number;
  };
  tenants: AdminTenantOverviewItem[];
  ruleBundle: AdminRuleBundleView;
}

export interface AdminAuditEntry {
  id: string;
  action: AdminAuditAction;
  actorType: AdminAuditActorType;
  actorId: string;
  actorEmail?: string;
  adminId?: string;
  adminEmail?: string;
  tenantId?: string;
  resourceType: string;
  resourceId: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AdminAuditListResponse {
  entries: AdminAuditEntry[];
}

export interface AdminBillingOrderView extends BillingOrderView {
  tenantName: string;
  userEmail?: string;
  userName?: string;
}

export interface AdminBillingOrderListResponse {
  orders: AdminBillingOrderView[];
}

export interface AdminBillingSettingsResponse {
  settings: AdminBillingSettingsView;
}

/**
 * 单账号云同步管理视图（apps/cloud-admin TenantDetailPage 「云同步」Card 数据源）。
 *
 * 仅返回元数据聚合：requirements §3.5 规定云端只持有密文与必要元数据，
 * admin 接口不外泄 saltB64 / encryptedRootKeyB64 / 完整 jwk / 任何明文字段。
 */
/**
 * 服务端托管 root_key 摘要。
 *
 * 仅返回元数据：version / masterKeyId / 时间戳。
 * **不**返回 rootKeyB64 / nonce / ciphertext —— admin UI 不需要解密能力。
 */
export interface AdminSyncRootKeySummary {
  version: number | null;
  /** master KEK 标识（运营定位）。 */
  masterKeyId: string | null;
  createdAt: string | null;
  rotatedAt: string | null;
  /** 上一版本号（仅在轮换后有值）。 */
  rotatedFromVersion: number | null;
}

export interface AdminSyncDeviceView {
  /** sync_devices.id，用于 `POST .../sync/devices/:deviceId/revoke`。 */
  id: string;
  /** 业务上的设备 id（`syncDevice.deviceId`）。 */
  deviceId: string;
  deviceName: string | null;
  status: "active" | "revoked";
  trustedAt: string;
  revokedAt: string | null;
  lastSeenAt: string;
  /** 公钥指纹：sha256(jwk-canonical) 前 8 字节 hex；不暴露完整 jwk。 */
  publicKeyJwkThumbprint: string | null;
}

export interface AdminSyncBlobAggregate {
  /** `store_credential_v1` / `browser_profile_archive_v1` / 其它历史 kind。 */
  kind: string;
  /** 该 kind 下未删除的 blob 行数。 */
  count: number;
  /** 总占用密文字节数。 */
  totalBytes: number;
  /** 该 kind 下最高 version。 */
  latestVersion: number;
  /** 该 kind 下最近 updatedAt（UTC ISO 8601）。 */
  latestUpdatedAt: string | null;
}

export interface AdminSyncRecentAuditEntry {
  id: string;
  action: AdminAuditAction;
  outcome: string;
  errorCode: string | null;
  actorEmail: string | null;
  deviceId: string | null;
  createdAt: string;
}

export interface AdminTenantSyncOverview {
  tenantId: string;
  rootKey: AdminSyncRootKeySummary;
  devices: AdminSyncDeviceView[];
  /** 按 kind 聚合的密文用量；空数组表示从未上传。 */
  blobAggregates: AdminSyncBlobAggregate[];
  /** 按 createdAt 倒序的最近最多 5 条 `cloud.sync.*` 审计记录。 */
  recentAudits: AdminSyncRecentAuditEntry[];
  /** 整个账号主体的 blob 总数（≡ Σ blobAggregates.count）。 */
  blobCount: number;
  /** 整个账号主体的密文总占用 bytes。 */
  totalBlobBytes: number;
  /** 整个账号主体最近一次 sync push / pull 时间（取最大）。 */
  lastSyncedAt: string | null;
}

export interface AdminTenantSyncOverviewResponse {
  overview: AdminTenantSyncOverview;
}

export interface AdminSyncDeviceRevokeResponse {
  device: AdminSyncDeviceView;
}
